import { useEffect, useState } from "react";
import { api, adminAuth } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, ShieldCheck, Hash, Mail } from "lucide-react";

export default function TaxExemptTab() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ type: "code", value: "", label: "", certificate_number: "" });

  const load = async () => {
    try { const r = await api.get("/admin/tax-exemptions", { headers: adminAuth() }); setList(r.data); }
    catch { toast.error("Couldn't load tax exemptions"); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.value.trim()) { toast.error("Enter a code or email"); return; }
    try {
      await api.post("/admin/tax-exemptions", form, { headers: adminAuth() });
      setForm({ type: "code", value: "", label: "", certificate_number: "" });
      toast.success("Exemption added");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggle = async (d) => {
    const next = !d.is_active;
    setList((prev) => prev.map((x) => x.id === d.id ? { ...x, is_active: next } : x));
    try { await api.patch(`/admin/tax-exemptions/${d.id}`, { is_active: next }, { headers: adminAuth() }); }
    catch { setList((prev) => prev.map((x) => x.id === d.id ? { ...x, is_active: d.is_active } : x)); toast.error("Update failed"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete tax exemption "${d.value}"?`)) return;
    try { await api.delete(`/admin/tax-exemptions/${d.id}`, { headers: adminAuth() }); load(); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="card-rustic" data-testid="admin-tax-exempt">
      <div className="p-5 border-b border-[rgba(27,42,65,0.1)]">
        <div className="font-mono-ui text-[10px] tracking-[0.3em] text-[var(--crimson)]">/TAX EXEMPT</div>
        <div className="font-serif-display text-2xl text-[var(--navy)]">Tax-Exempt Customers</div>
        <div className="text-xs text-[var(--ink-soft)] mt-1">Add a private <strong>code</strong> to hand out, or an <strong>email</strong> that's always exempt. Matching checkouts skip sales tax. Keep the customer's resale/exemption certificate # on file here for your records.</div>
      </div>

      <form onSubmit={create} className="p-5 border-b border-[rgba(27,42,65,0.06)] grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <label className="block">
          <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--ink-soft)] block mb-1">Type</span>
          <select className="input-field" value={form.type} onChange={(e)=>setForm({...form, type: e.target.value})} data-testid="exempt-type-select">
            <option value="code">Code</option>
            <option value="email">Email</option>
          </select>
        </label>
        <label className="block">
          <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--ink-soft)] block mb-1">{form.type === "code" ? "Code" : "Email"}</span>
          <input className="input-field" placeholder={form.type === "code" ? "RESALE-2026" : "buyer@shop.com"} value={form.value} onChange={(e)=>setForm({...form, value: e.target.value})} data-testid="exempt-value-input"/>
        </label>
        <label className="block">
          <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--ink-soft)] block mb-1">Label (who)</span>
          <input className="input-field" placeholder="ABC Boutique" value={form.label} onChange={(e)=>setForm({...form, label: e.target.value})} data-testid="exempt-label-input"/>
        </label>
        <label className="block">
          <span className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--ink-soft)] block mb-1">Certificate #</span>
          <input className="input-field" placeholder="TX resale #" value={form.certificate_number} onChange={(e)=>setForm({...form, certificate_number: e.target.value})} data-testid="exempt-cert-input"/>
        </label>
        <button className="btn-primary justify-center" data-testid="exempt-create-button">Add</button>
      </form>

      <div className="divide-y divide-[rgba(27,42,65,0.06)]">
        {list.map((d) => (
          <div key={d.id} className="p-4 flex items-center justify-between gap-4" data-testid={`exempt-row-${d.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 grid place-items-center rounded-sm bg-[var(--bone-2)] text-[var(--crimson)]">
                {d.type === "code" ? <Hash className="w-4 h-4"/> : <Mail className="w-4 h-4"/>}
              </div>
              <div>
                <div className="font-mono-ui text-sm text-[var(--navy)] flex items-center gap-2">
                  {d.value}
                  <span className={`inline-block px-2 py-0.5 rounded-full font-mono-ui text-[9px] tracking-[0.15em] uppercase ${d.is_active ? "bg-[var(--gold)]/25 text-[var(--navy)]" : "bg-[var(--ink-soft)]/15 text-[var(--ink-soft)]"}`} data-testid={`exempt-status-${d.id}`}>
                    {d.is_active ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="text-[11px] text-[var(--ink-soft)] mt-0.5">
                  {d.label || "—"}{d.certificate_number ? ` · Cert #${d.certificate_number}` : ""}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button type="button" role="switch" aria-checked={d.is_active} onClick={() => toggle(d)} data-testid={`exempt-toggle-${d.id}`}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${d.is_active ? "bg-[var(--crimson)]" : "bg-[var(--ink-soft)]/40"}`} title={d.is_active ? "Disable" : "Enable"}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${d.is_active ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
              <button type="button" onClick={() => remove(d)} data-testid={`exempt-delete-${d.id}`}
                className="inline-flex items-center gap-1.5 text-[var(--crimson)] hover:bg-[var(--crimson)]/10 px-2 py-1.5 rounded-sm font-mono-ui text-[10px] tracking-[0.2em] uppercase transition" title="Delete">
                <Trash2 className="w-3.5 h-3.5"/> Delete
              </button>
            </div>
          </div>
        ))}
        {list.length === 0 && (
          <div className="p-8 text-center text-[var(--ink-soft)] text-sm flex flex-col items-center gap-2">
            <ShieldCheck className="w-6 h-6 opacity-50"/>
            No tax exemptions yet. Add a code or email above.
          </div>
        )}
      </div>
    </div>
  );
}
